import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Spinner } from './Spinner'
import { Skeleton } from '@/components/ui/skeleton'
import { loaderMessages } from './loader-messages'

const pageLoaderVariants = cva(
  'fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm',
  {
    variants: {
      variant: {
        spinner: '',
        skeleton: '',
        minimal: 'bg-transparent backdrop-blur-none',
      },
    },
    defaultVariants: {
      variant: 'spinner',
    },
  }
)

export interface PageLoaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof pageLoaderVariants> {
  message?: string
  skeletonType?: 'card' | 'table' | 'list' | 'custom'
  customSkeleton?: React.ReactNode
}

const PageLoader = React.forwardRef<HTMLDivElement, PageLoaderProps>(
  (
    {
      className,
      variant = 'spinner',
      message = loaderMessages.page,
      skeletonType = 'card',
      customSkeleton,
      ...props
    },
    ref
  ) => {
    const renderContent = () => {
      if (variant === 'skeleton') {
        if (customSkeleton) {
          return customSkeleton
        }

        switch (skeletonType) {
          case 'table':
            return (
              <div className="w-full max-w-4xl p-6 space-y-4">
                <Skeleton className="h-8 w-64 mb-6" />
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              </div>
            )
          case 'list':
            return (
              <div className="w-full max-w-2xl p-6 space-y-4">
                <Skeleton className="h-8 w-64 mb-6" />
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              </div>
            )
          case 'card':
          default:
            return (
              <div className="w-full max-w-4xl p-6 space-y-4">
                <Skeleton className="h-8 w-64 mb-6" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-32 w-full" />
                  ))}
                </div>
              </div>
            )
        }
      }

      return (
        <div className="flex flex-col items-center justify-center gap-4">
          <Spinner size="xl" variant="primary" />
          <p className="text-sm font-medium text-muted-foreground">{message}</p>
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className={cn(pageLoaderVariants({ variant }), className)}
        {...props}
      >
        {renderContent()}
      </div>
    )
  }
)
PageLoader.displayName = 'PageLoader'

export { PageLoader }

